import { useState } from "react";
import { getNamespaceTranslations } from "../lib/questionnaire-translations";
import { focusFirstInvalidField } from "../lib/focus-first-invalid";
import { FormField } from "./ui/FormField";
import { PrimaryButton } from "./ui/PrimaryButton";
import { ReassuranceLine } from "./ui/ReassuranceLine";

interface AccountEmailProps {
  onContinue: (email: string, tnc: boolean) => void;
  language?: "en" | "es";
  logoUrl?: string;
  loading?: boolean;
}

export function AccountEmail({ onContinue, language = "en", loading = false }: AccountEmailProps) {
  const t = getNamespaceTranslations(language, "account")!;
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [tnc, setTnc] = useState(true);
  const [tncTouched, setTncTouched] = useState(false);

  // Validate email format and TLD (minimum 2 characters)
  const isValidEmail = (emailAddress: string): boolean => {
    const basicFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddress);
    if (!basicFormat) return false;
    // Check TLD is at least 2 characters
    const tldMatch = emailAddress.match(/\.([a-zA-Z]+)$/);
    return tldMatch ? tldMatch[1].length >= 2 : false;
  };

  const showTncRequiredError = tncTouched && !tnc;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError(t.fieldRequired || t.emailError);
      focusFirstInvalidField(["email"]);
      return;
    }
    if (!isValidEmail(email)) {
      setError(t.emailError);
      focusFirstInvalidField(["email"]);
      return;
    }
    if (!tnc) {
      setTncTouched(true);
      focusFirstInvalidField(["tnc"]);
      return;
    }
    setError("");
    onContinue(email, tnc);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 mt-8 gap-y-[30px]">
      {/* Content */}
      <div className="flex-1 pb-0">
        <div className="space-y-[30px]">
          <div className="space-y-2.5">
            <h1
              className="text-[24px] sm:!text-[30px] leading-[30px] sm:!leading-[36px] font-normal text-black"
              style={{ fontFamily: "TT Commons Pro" }}
            >
              {t.emailTitle}
            </h1>
            <p
              className="text-[16px] leading-[22px] text-gray-700"
              style={{ fontFamily: "TT Commons Pro" }}
            >
              {t.emailSubtitle}
            </p>
          </div>

          {/* Email Field */}
          <FormField
            id="email"
            type="email"
            label={t.emailLabel}
            value={email}
            onChange={(value) => {
              setEmail(value);
              if (error) setError(""); // Clear error on change
            }}
            placeholder={t.emailPlaceholder}
            error={error}
            required
            autoComplete="email"
            testId="email_address_start-input"
          />

          {/* Terms & Conditions */}
          <div className="space-y-3 text-sm">
            <label htmlFor="tnc" className="flex items-start gap-3">
              <input
                id="tnc"
                type="checkbox"
                name="tnc"
                checked={tnc}
                onChange={(e) => {
                  setTncTouched(true);
                  setTnc(e.target.checked);
                }}
                className="mt-1 h-6 w-6 flex-shrink-0 rounded border-gray-300 accent-[#2D55FB]"
                aria-required="true"
                aria-invalid={showTncRequiredError}
                data-testid="email_checkbox-tnc"
              />
              <span
                dangerouslySetInnerHTML={{ __html: (t as { tncLabel: string }).tncLabel }}
                className="text-gray-700 leading-[20px]"
              />
            </label>
            {showTncRequiredError && (
              <p className="text-sm text-[#E34D52]" role="alert">{t.tncRequiredError || t.fieldRequired}</p>
            )}
          </div>
        </div>
      </div>

      {/* Continue Button — always full colour; validate on submit */}
      <div className="space-y-3">
        <PrimaryButton
          type="submit"
          isLoading={loading}
          data-testid="email_continue-button"
        >
          {t.emailContinueButton}
        </PrimaryButton>
        <ReassuranceLine text={t.emailReassurance} />
      </div>
    </form>
  );
}
